// The screens around a Stripe purchase: the return from Checkout (#/subscribed?session_id=…), restoring on this
// device (#/restore, by e-mail link or transfer code; #/restore?code=… redeems an e-mailed link) and
// More › TrailBivvy Pro (#/more/pro: status, manage on Stripe, transfer code, refresh, remove) — decisions.md PWA-16.

import { useEffect, useState } from 'preact/hooks';
import { date as formatDate, dayTime as formatDateTime } from '../../i18n/format';
import { locale, t } from '../../i18n';
import { subscriptionApi, ApiError } from '../../store/api';
import { statusLine } from '../../store/paywall';
import { Banner, Button, Card, NavBar, Row, Section, Spinner } from '../components/ui';
import { useEnv } from '../env';
import { href, navigate, route } from '../router';
import { LegalLinks, PaywallValueList } from './PaywallScreen';

function errorText(code: string): string {
  switch (code) {
    case 'invalid_code':
      return t('pwa.restore.invalidCode');
    case 'none':
      return t('pwa.restore.none');
    case 'network':
      return t('pwa.restore.offline');
    case 'rate_limited':
      return t('pwa.restore.tooMany');
    case 'email_unavailable':
      return t('pwa.restore.email.unavailable');
    default:
      return t('pwa.error.generic');
  }
}

/** Return from Stripe Checkout: exchange the session for the entitlement token. */
export function SubscribedScreen() {
  const env = useEnv();
  const sessionId = route.value.query.get('session_id') ?? '';
  const [state, setState] = useState<'checking' | 'done' | 'failed'>('checking');

  const confirm = async () => {
    setState('checking');
    const result = await env.entitlement.completeCheckout(sessionId);
    setState(result.ok ? 'done' : 'failed');
  };

  useEffect(() => {
    void confirm();
  }, [sessionId]);

  return (
    <div class="screen">
      <div class="screen-inner stack">
        <NavBar title={t('pwa.subscribed.title')} />
        <Card>
          {state === 'checking' && <Spinner label={t('pwa.subscribed.checking')} />}
          {state === 'done' && (
            <>
              <Banner tone="positive" icon="checkCircle" role="status">
                {t('pwa.subscribed.done')}
              </Banner>
              <Button variant="primary" onClick={() => navigate('route/catalog', { replace: true })}>
                {t('pwa.subscribed.continue')}
              </Button>
            </>
          )}
          {state === 'failed' && (
            <>
              <Banner tone="caution" role="alert">
                {t('pwa.subscribed.failed')}
              </Banner>
              <Button variant="primary" icon="refresh" onClick={confirm}>
                {t('common.retry')}
              </Button>
              <Button href={href('restore')}>{t('pwa.restore.title')}</Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

export function RestoreScreen() {
  const env = useEnv();
  const query = route.value.query;
  const linkCode = query.get('code');
  const [email, setEmail] = useState(query.get('email') ?? env.prefs.subscriptionEmail.value ?? '');
  const [transferCode, setTransferCode] = useState('');
  const [emailState, setEmailState] = useState<{ busy: boolean; message?: string; tone?: 'positive' | 'negative' }>({ busy: false });
  const [codeState, setCodeState] = useState<{ busy: boolean; message?: string }>({ busy: false });
  const [linkState, setLinkState] = useState<'idle' | 'checking' | 'failed'>(linkCode ? 'checking' : 'idle');
  const [linkError, setLinkError] = useState<string | undefined>();
  const entitled = env.entitlement.access.value === 'entitled';

  useEffect(() => {
    if (!linkCode) return;
    void (async () => {
      const result = await env.entitlement.redeemRestoreCode(linkCode);
      if (result.ok) {
        setLinkState('idle');
        // Drop the code from the address bar and history.
        navigate('restore', { replace: true });
      } else {
        setLinkState('failed');
        setLinkError(errorText(result.code));
      }
    })();
  }, [linkCode]);

  const sendLink = async (e: Event) => {
    e.preventDefault();
    if (!env.isOnline.value) {
      setEmailState({ busy: false, message: t('pwa.restore.offline'), tone: 'negative' });
      return;
    }
    setEmailState({ busy: true });
    try {
      await subscriptionApi.restoreByEmail(email.trim().toLowerCase(), locale.value);
      env.prefs.subscriptionEmail.value = email.trim().toLowerCase();
      setEmailState({ busy: false, message: t('pwa.restore.email.sent'), tone: 'positive' });
    } catch (error) {
      const code = error instanceof ApiError ? (error.isNetwork ? 'network' : error.code) : 'unknown';
      setEmailState({ busy: false, message: code === 'invalid_email' ? t('pwa.paywall.email.invalid') : errorText(code), tone: 'negative' });
    }
  };

  const useCode = async (e: Event) => {
    e.preventDefault();
    setCodeState({ busy: true });
    const result = await env.entitlement.redeemTransferCode(transferCode);
    setCodeState({ busy: false, message: result.ok ? undefined : errorText(result.code) });
  };

  return (
    <div class="screen">
      <div class="screen-inner stack">
        <NavBar title={t('pwa.restore.title')} back={{ onClick: () => history.back(), label: t('common.close') }} />
        {entitled ? (
          <Card>
            <Banner tone="positive" icon="checkCircle" role="status">
              {t('pwa.restore.done')}
            </Banner>
            <Button variant="primary" onClick={() => navigate('route/catalog', { replace: true })}>
              {t('pwa.subscribed.continue')}
            </Button>
          </Card>
        ) : (
          <>
            {linkState === 'checking' && (
              <Card>
                <Spinner label={t('pwa.restore.link.checking')} />
              </Card>
            )}
            {linkState === 'failed' && linkError && <Banner tone="negative" role="alert">{linkError}</Banner>}
            <p class="body secondary">{t('pwa.restore.intro')}</p>
            <Card>
              <h2 class="title3">{t('pwa.restore.email.title')}</h2>
              <p class="footnote">{t('pwa.restore.email.detail')}</p>
              <form class="stack-s" onSubmit={sendLink}>
                <div class="field">
                  <label for="restore-email">{t('pwa.paywall.email.label')}</label>
                  <input
                    id="restore-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    spellcheck={false}
                    required
                    value={email}
                    onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
                  />
                </div>
                {emailState.message && (
                  <Banner tone={emailState.tone === 'positive' ? 'positive' : 'negative'} role="status">
                    {emailState.message}
                  </Banner>
                )}
                <Button variant="primary" type="submit" icon="mail" busy={emailState.busy}>
                  {t('pwa.restore.email.send')}
                </Button>
              </form>
            </Card>
            <Card>
              <h2 class="title3">{t('pwa.restore.code.title')}</h2>
              <p class="footnote">{t('pwa.restore.code.detail')}</p>
              <form class="stack-s" onSubmit={useCode}>
                <div class="field">
                  <label for="restore-code">{t('pwa.restore.code.label')}</label>
                  <input
                    id="restore-code"
                    class="mono"
                    autoComplete="one-time-code"
                    autoCapitalize="characters"
                    spellcheck={false}
                    maxLength={12}
                    value={transferCode}
                    onInput={(e) => setTransferCode((e.currentTarget as HTMLInputElement).value.toUpperCase())}
                  />
                </div>
                {codeState.message && (
                  <Banner tone="negative" role="alert">
                    {codeState.message}
                  </Banner>
                )}
                <Button type="submit" icon="key" busy={codeState.busy} disabled={transferCode.replace(/[^0-9A-Z]/gi, '').length !== 8}>
                  {t('pwa.restore.code.submit')}
                </Button>
              </form>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

export function ProScreen() {
  const env = useEnv();
  const details = env.entitlement.details.value;
  const access = env.entitlement.access.value;
  const line = statusLine(details);
  const [transfer, setTransfer] = useState<{ code: string; expiresAt: number } | undefined>();
  const [message, setMessage] = useState<string | undefined>();
  const [busy, setBusy] = useState<string | undefined>();

  const statusText = (() => {
    if (!env.paywallEnabled) return t('more.pro.allIncluded');
    if (!line) return t('pwa.pro.notSubscribed');
    switch (line.key) {
      case 'trialRenews':
        return t('subscription.status.trialRenews', formatDate(line.date));
      case 'trialEnds':
        return t('subscription.status.trialEnds', formatDate(line.date));
      case 'activeRenews':
        return t('subscription.status.activeRenews', formatDate(line.date));
      case 'activeEnds':
        return t('subscription.status.activeEnds', formatDate(line.date));
      case 'billingProblem':
        return t('pwa.paywall.billingProblem');
      case 'renewalUnconfirmed':
        return t('subscription.status.renewalUnconfirmed');
      case 'ended':
        return t('subscription.status.ended');
    }
  })();

  const manage = async () => {
    setBusy('manage');
    setMessage(undefined);
    const result = await env.entitlement.portalUrl(locale.value);
    setBusy(undefined);
    if ('url' in result) window.location.assign(result.url);
    else setMessage(errorText(result.error === 'network' ? 'network' : 'unknown'));
  };

  const getTransferCode = async () => {
    setBusy('transfer');
    setMessage(undefined);
    const result = await env.entitlement.createTransferCode();
    setBusy(undefined);
    if ('code' in result) setTransfer(result);
    else setMessage(errorText(result.error));
  };

  const refresh = async () => {
    setBusy('refresh');
    const result = await env.entitlement.refresh();
    setBusy(undefined);
    setMessage(result.ok ? undefined : errorText(result.code));
  };

  const hasToken = details !== undefined;
  const lastConfirmed = env.entitlement.lastConfirmedAt.value;

  return (
    <div class="screen">
      <div class="screen-inner stack">
        <NavBar title={t('more.pro')} back={{ href: href('more'), label: t('tab.more') }} />
        <Card>
          <p class="headline" role="status">
            {statusText}
          </p>
          {lastConfirmed && hasToken && <p class="footnote">{t('pwa.pro.lastChecked', formatDateTime(lastConfirmed))}</p>}
          {message && <Banner tone="negative">{message}</Banner>}
          {!hasToken && access !== 'entitled' && (
            <div class="button-stack">
              <Button variant="primary" href={href('paywall')}>
                {t('pwa.pro.subscribe')}
              </Button>
              <Button href={href('restore')}>{t('pwa.restore.title')}</Button>
            </div>
          )}
        </Card>
        {hasToken && (
          <Section footer={t('pwa.pro.manage.detail')}>
            <Row icon="card" title={t('subscription.manage')} onClick={manage} accessory={busy === 'manage' ? <span class="spinner" /> : undefined} external />
            <Row icon="refresh" title={t('pwa.pro.refresh')} onClick={refresh} accessory={busy === 'refresh' ? <span class="spinner" /> : undefined} />
          </Section>
        )}
        {access === 'entitled' && hasToken && (
          <Section header={t('pwa.pro.transfer.title')} footer={t('pwa.pro.transfer.detail')}>
            {transfer ? (
              <Row
                icon="key"
                title={<span class="mono" style={{ fontSize: '28px', letterSpacing: '0.12em' }}>{`${transfer.code.slice(0, 4)} ${transfer.code.slice(4)}`}</span>}
                subtitle={t('pwa.pro.transfer.expires', formatDateTime(transfer.expiresAt * 1000))}
              />
            ) : (
              <Row icon="key" title={t('pwa.pro.transfer.button')} onClick={getTransferCode} accessory={busy === 'transfer' ? <span class="spinner" /> : undefined} />
            )}
          </Section>
        )}
        <Section header={t('subscription.included')}>
          <div style={{ padding: '16px' }}>
            <PaywallValueList />
          </div>
        </Section>
        {hasToken && (
          <Section footer={t('pwa.pro.remove.detail')}>
            <Row icon="trash" iconTone="negative" title={t('pwa.pro.remove')} onClick={() => void env.entitlement.removeFromDevice()} />
          </Section>
        )}
        <p class="footnote">{t('pwa.pro.privacy')}</p>
        <LegalLinks />
      </div>
    </div>
  );
}
